import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type AppSelectOption = {
  readonly value: string;
  readonly label: string;
  readonly group?: string;
};

type MenuBox = {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly maxHeight?: number;
};

function shouldShowGroups(options: readonly AppSelectOption[]): boolean {
  const groups = new Set(
    options.flatMap((option) =>
      option.group === undefined ? [] : [option.group],
    ),
  );
  if (groups.size <= 1) return false;
  return options.some(
    (option) =>
      option.group !== undefined &&
      option.label.trim().toLowerCase() !== option.group.toLowerCase(),
  );
}

export function scrollOptionIntoList(
  list: HTMLElement,
  option: HTMLElement,
): void {
  const listRect = list.getBoundingClientRect();
  const optionRect = option.getBoundingClientRect();
  if (optionRect.top < listRect.top) {
    list.scrollTop -= listRect.top - optionRect.top;
    return;
  }
  if (optionRect.bottom > listRect.bottom) {
    list.scrollTop += optionRect.bottom - listRect.bottom;
  }
}

function menuBoxForTrigger(
  trigger: HTMLButtonElement,
  matchTriggerWidth: boolean,
  fitContent: boolean,
): MenuBox {
  const rect = trigger.getBoundingClientRect();
  const width = matchTriggerWidth ? rect.width : Math.max(rect.width, 256);
  const viewportPadding = 8;
  const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
  const spaceAbove = rect.top - viewportPadding;
  const openUp = !fitContent && spaceBelow < 160 && spaceAbove > spaceBelow;
  const maxHeight = fitContent
    ? undefined
    : Math.max(120, (openUp ? spaceAbove : spaceBelow) - 6);
  const left = Math.min(
    Math.max(viewportPadding, rect.left),
    Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
  );
  const gap = -1;
  return {
    top: openUp
      ? Math.max(viewportPadding, rect.top - (maxHeight ?? 0) - gap)
      : rect.bottom + gap,
    left,
    width,
    ...(maxHeight === undefined ? {} : { maxHeight }),
  };
}

export function AppSelect({
  id,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select",
  footerAction,
  matchTriggerWidth = false,
  fitContent = false,
  className,
  menuClassName,
  "aria-label": ariaLabel,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly AppSelectOption[];
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly footerAction?:
    | {
        readonly label: string;
        readonly onSelect: () => void;
      }
    | undefined;
  readonly matchTriggerWidth?: boolean;
  readonly fitContent?: boolean;
  readonly className?: string;
  readonly menuClassName?: string;
  readonly "aria-label": string;
}) {
  const generatedId = useId();
  const listId = `${id}-list`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef("");
  const searchTimerRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<MenuBox | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const [activeIndex, setActiveIndex] = useState(
    selectedIndex >= 0 ? selectedIndex : 0,
  );

  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const triggerDisabled =
    disabled || (options.length === 0 && footerAction === undefined);
  const showGroups = shouldShowGroups(options);
  const menuReady = open && menuBox !== null;

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (index: number) => {
    const option = options[index];
    if (option === undefined) return;
    onChange(option.value);
    close();
  };

  const moveActive = (nextIndex: number) => {
    if (options.length === 0) return;
    const wrapped =
      ((nextIndex % options.length) + options.length) % options.length;
    setActiveIndex(wrapped);
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    const update = () => {
      const trigger = triggerRef.current;
      if (trigger === null) return;
      setMenuBox(menuBoxForTrigger(trigger, matchTriggerWidth, fitContent));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [fitContent, matchTriggerWidth, open]);

  useLayoutEffect(() => {
    if (!menuReady) return;
    if (options.length === 0 && footerAction !== undefined) {
      footerRef.current?.focus();
      return;
    }
    listRef.current?.focus();
  }, [footerAction, menuReady, options.length]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open, selectedIndex]);

  useEffect(() => {
    return () => {
      window.clearTimeout(searchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const option = menuRef.current?.querySelector(
      `[data-option-index="${String(activeIndex)}"]`,
    );
    if (list instanceof HTMLElement && option instanceof HTMLElement) {
      scrollOptionIntoList(list, option);
    }
  }, [activeIndex, open]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (
        footerAction !== undefined &&
        (options.length === 0 || activeIndex >= options.length - 1)
      ) {
        footerRef.current?.focus();
        return;
      }
      moveActive(activeIndex + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(activeIndex - 1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      moveActive(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      moveActive(options.length - 1);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(activeIndex);
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (event.key.length === 1 && event.key !== " ") {
      window.clearTimeout(searchTimerRef.current);
      searchRef.current += event.key.toLowerCase();
      const query = searchRef.current;
      const match = options.findIndex((option) =>
        option.label.toLowerCase().startsWith(query),
      );
      if (match >= 0) setActiveIndex(match);
      searchTimerRef.current = window.setTimeout(() => {
        searchRef.current = "";
      }, 500);
    }
  };

  const activeOption = options[activeIndex];
  const activeOptionId =
    activeOption === undefined
      ? undefined
      : `${generatedId}-option-${activeOption.value}`;

  let lastGroup: string | undefined;
  const menuItems: ReactNode[] = [];
  for (const [index, option] of options.entries()) {
    const optionId = `${generatedId}-option-${option.value}`;
    const group = option.group;
    if (group !== undefined) {
      if (showGroups && group !== lastGroup) {
        menuItems.push(
          <div className="app-select__group" key={`group-${group}`}>
            {group}
          </div>,
        );
      }
      lastGroup = group;
    }
    menuItems.push(
      <div
        className={`app-select__option${
          option.value === value ? " app-select__option--selected" : ""
        }${index === activeIndex ? " app-select__option--active" : ""}`}
        data-option-index={index}
        id={optionId}
        key={option.value}
        role="option"
        aria-selected={option.value === value}
        onMouseEnter={() => {
          setActiveIndex(index);
        }}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={() => {
          choose(index);
        }}
      >
        {option.label}
      </div>,
    );
  }

  return (
    <div
      className={
        className === undefined ? "app-select" : `app-select ${className}`
      }
    >
      <button
        className="app-select__trigger"
        type="button"
        id={id}
        ref={triggerRef}
        disabled={triggerDisabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          setOpen((current) => !current);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="app-select__value">
          {selected?.label ?? placeholder}
        </span>
        <svg
          className={`app-select__chevron${
            open ? " app-select__chevron--open" : ""
          }`}
          viewBox="0 0 12 12"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <path d="M2.5 4.5 6 8l3.5-3.5" />
        </svg>
      </button>
      {open && menuBox !== null
        ? createPortal(
            <div
              className={[
                "app-select__menu",
                matchTriggerWidth ? "app-select__menu--flush" : "",
                fitContent ? "app-select__menu--fit" : "",
                menuClassName ?? "",
              ]
                .filter((part) => part.length > 0)
                .join(" ")}
              ref={menuRef}
              style={{
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                ...(menuBox.maxHeight === undefined
                  ? {}
                  : { maxHeight: menuBox.maxHeight }),
              }}
            >
              <div
                className="app-select__list"
                id={listId}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
                aria-activedescendant={activeOptionId}
                aria-label={ariaLabel}
                onKeyDown={onListKeyDown}
              >
                {menuItems}
              </div>
              {footerAction === undefined ? null : (
                <button
                  className="app-select__footer"
                  type="button"
                  ref={footerRef}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    footerAction.onSelect();
                    close();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      close();
                      return;
                    }
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      listRef.current?.focus();
                      setActiveIndex(Math.max(0, options.length - 1));
                    }
                  }}
                >
                  {footerAction.label}
                </button>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
